<template>
  <auth-shell
    :title="$t('auth.login.title')"
    :subtitle="$t('auth.login.sub')"
    :back-label="$t('auth.backHome')"
  >
    <form class="ps-auth-form" novalidate @submit.prevent="userLogin(form)">
      <!-- email -->
      <div class="ps-field">
        <label class="ps-field__label" for="loginEmail">{{
          $t("auth.login.email")
        }}</label>
        <input
          id="loginEmail"
          v-model="form.email"
          type="email"
          required
          autocomplete="email"
          :placeholder="$t('auth.login.emailPh', { at: '@' })"
          class="ps-input"
          :disabled="overlay"
          @input="$v.form.email.$touch()"
          @blur="$v.form.email.$touch()"
        />
        <p v-if="emailError.length" class="ps-field__error">
          {{ emailError[0] }}
        </p>
      </div>

      <!-- password -->
      <div class="ps-field">
        <div class="ps-field__row">
          <span class="ps-field__label" for="loginPassword">{{
            $t("auth.login.password")
          }}</span>
          <router-link to="/forgot-password" class="ps-field__link">{{
            $t("auth.login.forgot")
          }}</router-link>
        </div>
        <div class="ps-field__pw">
          <input
            id="loginPassword"
            v-model="form.password"
            :type="showPassword ? 'text' : 'password'"
            required
            autocomplete="current-password"
            :placeholder="$t('auth.login.passwordPh')"
            class="ps-input"
            :disabled="overlay"
            @input="$v.form.password.$touch()"
            @blur="$v.form.password.$touch()"
          />
          <button
            type="button"
            class="ps-field__eye"
            :aria-label="$t('show_password') || 'Show password'"
            @click="showPassword = !showPassword"
          >
            <v-icon small>{{
              showPassword ? "fal fa-eye-slash" : "fal fa-eye"
            }}</v-icon>
          </button>
        </div>
        <p v-if="passwordError.length" class="ps-field__error">
          {{ passwordError[0] }}
        </p>
      </div>

      <!-- remember me -->
      <label class="ps-check">
        <input v-model="rememberEmail" type="checkbox" class="ps-check__box" />
        <span>{{ $t("auth.login.remember") }}</span>
      </label>

      <button
        type="submit"
        class="ps-btn ps-btn--lime ps-btn--block ps-auth-submit"
        :disabled="overlay"
      >
        {{ overlay ? "…" : $t("auth.login.submit") }}
      </button>
    </form>

    <p class="ps-auth-switch">
      {{ $t("auth.login.haveAccount") }}
      <router-link to="/register" class="ps-auth-switch__link">{{
        $t("auth.login.register")
      }}</router-link>
    </p>

    <app-loading :overlay="overlay" />

    <app-custom-dialog
      v-model="dialog"
      title="choose_branch"
      @submit="setBranch()"
    >
      <div slot="activator">
        <p v-if="allBranchesExpired" class="text-caption grey--text px-4">
          {{ $t("no_active_branch") }}
        </p>
        <v-list>
          <v-list-item-group v-model="branch_id" mandatory color="primary">
            <v-list-item
              v-for="(branch, index) in branches"
              :key="index + branch.id + branch.name"
              :disabled="branch.is_expired"
              two-line
            >
              <template v-slot:default="{ active }">
                <v-list-item-avatar>
                  <app-img v-if="branch.logo" :img="branch.logo" />
                  <v-icon v-else>fas fa-store</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ branch.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{
                    branch.address
                  }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="d-flex flex-row align-center">
                  <branch-status-chip :branch="branch" class="mr-2" />
                  <v-checkbox
                    :input-value="active"
                    color="primary"
                    hide-details
                  ></v-checkbox>
                </v-list-item-action>
              </template>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </div>
    </app-custom-dialog>
  </auth-shell>
</template>

<script>
import { authenticated, setNewUser } from "@/controllers/auth";
import { toast } from "@/controllers/alert";
import AuthShell from "@/components/auth/AuthShell.vue";
import BranchStatusChip from "@/components/BranchStatusChip.vue";
import { login } from "@/validations/user";
import Cookies from "js-cookie";

export default {
  name: "LoginPage",
  layout: "blank",
  components: { AuthShell, BranchStatusChip },
  mixins: [login],
  data: () => ({
    rememberEmail: false,
    overlay: false,
    showPassword: false,
    form: {},
    dialog: false,
    branches: [],
    branch_id: 0,
    data: {},
  }),
  computed: {
    allBranchesExpired() {
      return this.branches.length > 0 && this.branches.every((b) => b.is_expired);
    },
  },
  mounted() {
    this.form.email = Cookies.get("email") || "";
    if (Cookies.get("remember")) {
      this.rememberEmail = true;
    } else {
      this.rememberEmail = false;
    }
  },
  watch: {
    rememberEmail(val) {
      if (val) {
        Cookies.set("remember", true, { expires: 30 });
      } else {
        Cookies.remove("remember");
      }
    },
  },
  methods: {
    async userLogin(form) {
      try {
        this.$v.form.$touch();
        if (!this.$v.form.$invalid) {
          this.overlay = true;
          const { data } = await this.$http.post("login", form);
          if (data.code === 403) {
            await setNewUser(this, data.data);
            this.$router.push("/open-store");
          } else if (data.status) {
            await toast(this, "login.success");
            if (this.rememberEmail) {
              Cookies.set("email", this.form.email, { expires: 30 });
            } else {
              Cookies.remove("email");
            }
            this.overlay = false;
            await authenticated(this, data);
          }
        }
      } catch (error) {
        this.overlay = false;
        // check status code is 403
        if (error.code === 403) {
          await setNewUser(this, error.data);
          this.$router.push("/open-store");
        }
      }
    },

    async fetchBranches() {
      try {
        const { data } = await this.$http.get("branches");
        if (data.status) {
          // check if branches is empty
          if (data.data.length === 0) {
            await toast(this, "no_branch");
          } else {
            this.branches = data.data;
            const firstActive = this.branches.findIndex((b) => !b.is_expired);
            this.branch_id = firstActive >= 0 ? firstActive : 0;
            this.dialog = true;
          }
        }
      } catch (error) {
        console.log(error);
      }
    },

    async setBranch() {
      try {
        if (this.allBranchesExpired) {
          // finish login; the first jwt call routes to /manage-branches where the owner can renew
          await authenticated(this, this.data);
          return;
        }
        // get id from branches[branch_id]
        const branch_id = this.branches[this.branch_id].id;
        const { data } = await this.$http.post(
          `set_branch/${branch_id}?skip=1`,
        );
        if (data.status) {
          await toast(this, "login.success");
          await authenticated(this, this.data);
        }
      } catch (error) {
        this.overlay = false;
        console.log(error);
      }
    },
  },
};
</script>

<style scoped>
.ps-auth-form {
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.ps-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ps-field__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.ps-field__label {
  font-size: 0.875rem;
  font-weight: 900;
  color: var(--ps-ink);
}

.ps-field__link {
  font-size: 12px;
  font-weight: 700;
  color: var(--ps-brand);
  text-decoration: none;
}
.ps-field__link:hover {
  text-decoration: underline;
}

.ps-field__pw {
  position: relative;
}
.ps-field__pw .ps-input {
  padding-right: 3rem;
}
.ps-field__eye {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: var(--ps-slate-400);
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s ease;
}
.ps-field__eye:hover {
  color: var(--ps-brand);
}

.ps-field__error {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  color: #dc2626;
}

.ps-check {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--ps-slate-600);
  cursor: pointer;
  width: fit-content;
}

.ps-check__box {
  width: 16px;
  height: 16px;
  accent-color: var(--ps-brand);
  cursor: pointer;
}

.ps-auth-submit {
  margin-top: 4px;
  padding: 1rem;
}

.ps-auth-switch {
  margin: 24px 0 0;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--ps-slate-500);
}

.ps-auth-switch__link {
  color: var(--ps-brand);
  font-weight: 900;
  text-decoration: none;
}
.ps-auth-switch__link:hover {
  text-decoration: underline;
}
</style>
